import React, { useState } from 'react';
import Header, { HeaderLeft } from '../../../layout/Header/Header';
import useDeviceScreen from '../../../hooks/useDeviceScreen';
import CommonHeaderRight from './CommonHeaderRight';
import CommonHeaderProfile from './CommonHeaderProfile';
import Icon from '../../../components/icon/Icon';
import { useAppSelector, useAppDispatch } from '../../../custom/redux/reduxHooks';

import LocationDetailsModal from './LocationDetailsModal';
import { toast } from 'react-toastify';
import api, { backendUrl } from '../../../custom/constants/urls';
import Popovers from '../../../components/bootstrap/Popovers';
import { formatMoney } from '../../../helpers/helpers';
import PermissionGuard from '../../../custom/components/PermissionGuard';
import GlobalSearch from '../../../custom/components/common/GlobalSearch';
import { useNavigate } from 'react-router-dom';
import { updateUserData } from '../../../custom/redux/reducers/authReducer';

const DefaultHeader = () => {
	const { width } = useDeviceScreen();
	const { user } = useAppSelector((state) => state.auth);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [showBalance, setShowBalance] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const [isSearchMode, setIsSearchMode] = useState(false);
	const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
	const [resetSearchTrigger, setResetSearchTrigger] = useState(false);

	const handleTransferClick = () => {
		navigate('/financial-management/transfer-management');
	};

	const handleRefreshData = async () => {
		try {
			setIsRefreshing(true);
			const response = await api.get(`${backendUrl}/admin/auth/getMe`);
			
			if (response.data.success) {
				dispatch(updateUserData(response.data));
				toast.success('Data refreshed successfully');
			}
		} catch (error) {
			toast.error(error.response?.data?.message || 'Failed to refresh data');
		} finally {
			setIsRefreshing(false);
		}
	};



	const toggleSearchMode = () => {
		if (isSearchMode) {
			setResetSearchTrigger(true);
			setTimeout(() => setResetSearchTrigger(false), 100);
		}
		setIsSearchMode(!isSearchMode);
	};

	return (
		<Header>
			<HeaderLeft>
				<div className='header-container'>
					<div className={`header-content ${isSearchMode ? 'search-active' : ''}`}>
						<div className='header-info-section'>
							<div 
								className='location-section clickable-location'
								onClick={() => setIsLocationModalOpen(true)}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										setIsLocationModalOpen(true);
									}
								}}
							>
								<div className='location-icon-wrapper'>
									<Icon
										icon='LocationOn'
										className='text-primary'
									/>
								</div>
								<div className='location-text'>
									<div className='text-muted small'>Current Location</div>
									<div className='fw-bold text-truncate'>{user?.userLocation?.locationName || "NA"}</div>
								</div>
								<div className='location-arrow'>
									<Icon
										icon='KeyboardArrowRight'
										className='text-muted'
									/>
								</div>
							</div>

							<div className='balance-wrapper'>
								<div className='balance-item'>
									<div className='balance-icon-wrapper'>
										<Icon
											icon='Store'
											className='text-success'
										/>
									</div>
									<div className='balance-content'>
										<div className='balance-header'>
											<div className='text-muted small'>Vault</div>
											<div className='d-flex align-items-center gap-1'>
												<Popovers
													trigger='hover'
													desc={isRefreshing ? 'Refreshing...' : 'Refresh Data'}>
													<Icon
														icon='Refresh'
														className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
														onClick={handleRefreshData}
														title='Refresh Balances'
													/>
												</Popovers>
												<Popovers
													trigger='hover'
													desc='Manage Transfers'>
													<Icon
														icon='SwapVert'
														className='transfer-icon'
														onClick={handleTransferClick}
														title='Go to Transfer Management'
													/>
												</Popovers>
											</div>
										</div>
										<div className='balance-amount-wrapper'>
											<div className='balance-amount text-success'>
												{showBalance
													? formatMoney(user?.userLocation?.locationBalance?.balance || 0, `${user?.userLocation?.currency || 'GMD'}`)
													: '••••••••'}
											</div>
											<Icon
												icon={showBalance ? 'VisibilityOff' : 'Visibility'}
												className='visibility-toggle'
												onClick={() => setShowBalance(!showBalance)}
											/>
										</div>
									</div>
								</div>

								<div className='balance-item'>
									<div className='balance-icon-wrapper'>
										<Icon
											icon='AccountBalanceWallet'
											className='text-primary'
										/>
									</div>
									<div className='balance-content'>
										<div className='balance-header'>
											<div className='text-muted small'>Your Balance</div>
											<Popovers
												trigger='hover'
												desc='Manage Transfers'>
												<Icon
													icon='SwapVert'
													className='transfer-icon'
													onClick={handleTransferClick}
													title='Go to Transfer Management'
												/>
											</Popovers>
										</div>
										<div className='balance-amount-wrapper'>
											<div className='balance-amount text-primary'>
												{formatMoney(user?.userBalance?.balance || 0, 'GMD')}
											</div>
										</div>
									</div>
								</div>
							</div>

							<button 
								className='search-toggle-btn'
								onClick={toggleSearchMode}
								aria-label='Toggle search'>
								<Icon icon='Search' />
							</button>
						</div>

						<div className='search-section'>
							<div className='search-wrapper'>
								<GlobalSearch resetTrigger={resetSearchTrigger} />
							</div>
							<Popovers
								trigger='hover'
								desc='Close Search Bar'>
								<button 
									className='search-close-btn'
									onClick={toggleSearchMode}
									aria-label='Close search'>
									<Icon icon='Close' />
								</button>
							</Popovers>
						</div>
					</div>
				</div>

				<style>
					{`
						.header-container {
							width: 100%;
							height: 60px;
							position: relative;
						}

						.header-content {
							width: 100%;
							height: 100%;
							position: relative;
							transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
						}

						.header-info-section {
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							height: 100%;
							display: flex;
							align-items: center;
							gap: 2rem;
							transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
							opacity: 1;
							transform: translateY(0);
						}

						.search-section {
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							height: 100%;
							display: flex;
							align-items: center;
							// gap: 1rem;
							// padding: 0 1rem;
							transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
							opacity: 0;
							transform: translateY(20px);
							pointer-events: none;
						}

						.search-active .header-info-section {
							opacity: 0;
							transform: translateY(-20px);
							pointer-events: none;
						}

						.search-active .search-section {
							opacity: 1;
							transform: translateY(0);
							pointer-events: all;
						}

						.location-section {
							display: flex;
							align-items: center;
							gap: 0.75rem;
							min-width: 200px;
						}

						.clickable-location {
							cursor: pointer;
							padding: 0.5rem;
							border-radius: 12px;
							transition: all 0.3s ease;
							position: relative;
							overflow: hidden;
						}

						.clickable-location::before {
							content: '';
							position: absolute;
							top: 0;
							left: -100%;
							width: 100%;
							height: 100%;
							background: linear-gradient(90deg, transparent, rgba(var(--bs-primary-rgb), 0.1), transparent);
							transition: left 0.5s;
						}

						.clickable-location:hover {
							background: rgba(var(--bs-primary-rgb), 0.05);
							transform: translateY(-1px);
							box-shadow: 0 4px 15px rgba(var(--bs-primary-rgb), 0.1);
						}

						.clickable-location:hover::before {
							left: 100%;
						}

						.clickable-location:active {
							transform: translateY(0);
							box-shadow: 0 2px 8px rgba(var(--bs-primary-rgb), 0.2);
						}

						.location-arrow {
							opacity: 0;
							transition: all 0.3s ease;
							transform: translateX(-10px);
						}

						.clickable-location:hover .location-arrow {
							opacity: 1;
							transform: translateX(0);
						}

						.location-icon-wrapper, .balance-icon-wrapper {
							display: flex;
							align-items: center;
							justify-content: center;
							width: 40px;
							height: 40px;
							border-radius: 10px;
							background: rgba(var(--bs-primary-rgb), 0.1);
							flex-shrink: 0;
						}

						.location-text {
							min-width: 0;
							flex: 1;
						}

						.balance-wrapper {
							display: flex;
							gap: 2rem;
							flex: 1;
						}

						.balance-item {
							display: flex;
							align-items: center;
							gap: 0.75rem;
							min-width: 180px;
						}

						.balance-content {
							min-width: 0;
							flex: 1;
						}

						.balance-header {
							display: flex;
							align-items: center;
							justify-content: space-between;
							margin-bottom: 0.25rem;
						}

						.balance-amount-wrapper {
							display: flex;
							align-items: center;
							gap: 0.5rem;
						}

						.balance-amount {
							font-weight: 600;
							font-family: 'Segoe UI', monospace;
							letter-spacing: 0.5px;
							font-size: 0.95rem;
						}

						.transfer-icon, .refresh-icon {
							font-size: 1.5rem;
							color: var(--bs-gray-600);
							cursor: pointer;
							transition: all 0.2s ease;
							padding: 4px;
							border-radius: 6px;
							background: rgba(var(--bs-gray-500), 0.08);
						}

						.transfer-icon:hover, .refresh-icon:hover {
							color: var(--bs-primary);
							background: rgba(var(--bs-primary-rgb), 0.15);
							transform: scale(1.15);
							box-shadow: 0 2px 8px rgba(var(--bs-primary-rgb), 0.2);
						}

						.refresh-icon.spinning {
							animation: spin 1s linear infinite;
							pointer-events: none;
							opacity: 0.7;
						}

						@keyframes spin {
							from {
								transform: rotate(0deg);
							}
							to {
								transform: rotate(360deg);
							}
						}

						.visibility-toggle {
							font-size: 1.1rem;
							color: var(--bs-gray-500);
							cursor: pointer;
							transition: all 0.2s ease;
						}

						.visibility-toggle:hover {
							color: var(--bs-primary);
						}

						.search-toggle-btn {
							display: flex;
							align-items: center;
							justify-content: center;
							width: 2.5rem;
							height: 2.5rem;
							border: none;
							border-radius: 50%;
							background: var(--bs-primary);
							color: var(--bs-white);
							cursor: pointer;
							transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
							flex-shrink: 0;
							box-shadow: 0 4px 15px rgba(var(--bs-primary-rgb), 0.3);
							position: relative;
							overflow: hidden;
						}

						.search-toggle-btn::before {
							content: '';
							position: absolute;
							top: 0;
							left: -100%;
							width: 100%;
							height: 100%;
							background: linear-gradient(90deg, transparent, rgba(var(--bs-white-rgb), 0.2), transparent);
							transition: left 0.6s;
						}

						.search-toggle-btn:hover {
							transform: translateY(-2px) scale(1.05);
							box-shadow: 0 8px 25px rgba(var(--bs-primary-rgb), 0.4);
						}

						.search-toggle-btn:hover::before {
							left: 100%;
						}

						.search-toggle-btn:active {
							transform: translateY(0) scale(0.98);
						}

						.search-wrapper {
							flex: 1;
							max-width: 600px;
							margin: 0;
						}

						.search-close-btn {
							display: flex;
							align-items: center;
							justify-content: center;
							width: 2.5rem;
							height: 2.5rem;
							border: none;
							border-radius: 50%;
							background: var(--bs-danger);
							color: var(--bs-white);
							cursor: pointer;
							transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
							flex-shrink: 0;
							box-shadow: 0 4px 15px rgba(var(--bs-danger-rgb), 0.3);
							position: relative;
							overflow: hidden;
						}

						.search-close-btn::before {
							content: '';
							position: absolute;
							top: 0;
							left: -100%;
							width: 100%;
							height: 100%;
							background: linear-gradient(90deg, transparent, rgba(var(--bs-white-rgb), 0.2), transparent);
							transition: left 0.6s;
						}

						.search-close-btn:hover {
							transform: translateY(-2px) scale(1.05);
							box-shadow: 0 8px 25px rgba(var(--bs-danger-rgb), 0.4);
						}

						.search-close-btn:hover::before {
							left: 100%;
						}

						.search-close-btn:active {
							transform: translateY(0) scale(0.98);
						}

						@media (max-width: 1200px) {
							.balance-wrapper {
								gap: 1.5rem;
							}
							.balance-item {
								min-width: 160px;
							}
							.header-info-section {
								gap: 1.5rem;
							}
						}

						@media (max-width: 992px) {
							.header-container {
								height: auto;
								min-height: 60px;
							}
							
							.header-info-section {
								flex-wrap: wrap;
								gap: 1rem;
								padding: 0.5rem 0;
							}
							
							.location-section {
								width: 100%;
								min-width: auto;
								order: 1;
							}
							
							.balance-wrapper {
								width: 100%;
								justify-content: space-between;
								gap: 1rem;
								order: 2;
							}
							
							.search-toggle-btn {
								order: 3;
								align-self: flex-start;
								margin-top: 0.5rem;
							}
							
							.balance-item {
								flex: 1;
								min-width: 140px;
							}
						}

						@media (max-width: 768px) {
							.balance-wrapper {
								flex-direction: column;
								gap: 0.75rem;
							}
							
							.balance-item {
								width: 100%;
								min-width: auto;
							}
							
							.search-toggle-btn {
								width: 2.25rem;
								height: 2.25rem;
							}

							.search-close-btn {
								width: 2.25rem;
								height: 2.25rem;
							}

							.search-section {
								padding: 0 0.75rem;
							}
						}

						@media (max-width: 576px) {
							.header-info-section {
								gap: 0.75rem;
							}
							
							.location-icon-wrapper, .balance-icon-wrapper {
								width: 36px;
								height: 36px;
							}
							
							.balance-amount {
								font-size: 0.875rem;
							}
							
							.transfer-icon, .refresh-icon {
								font-size: 1.35rem;
							}
							
							.visibility-toggle {
								font-size: 1rem;
							}
							
							.search-toggle-btn {
								width: 2rem;
								height: 2rem;
							}

							.search-close-btn {
								width: 2rem;
								height: 2rem;
							}

							.search-section {
								padding: 0 0.5rem;
							}
						}
					`}
				</style>
			</HeaderLeft>
			<CommonHeaderRight afterChildren={<CommonHeaderProfile />} />



			<LocationDetailsModal
				isOpen={isLocationModalOpen}
				setIsOpen={setIsLocationModalOpen}
				location={user?.userLocation}
			/>

		</Header>
	);
};

export default DefaultHeader;
